import React, { useState, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, FileText, RefreshCw, Filter } from "lucide-react";

const METHOD_COLOR = {
  POST: "bg-emerald-100 text-emerald-700",
  PUT: "bg-blue-100 text-blue-700",
  DELETE: "bg-red-100 text-red-700",
  PATCH: "bg-amber-100 text-amber-700",
};

const pathLabel = (path) => {
  const p = path.replace("/api/", "");
  const parts = p.split("/");
  const map = {
    invoices: "فواتير", customers: "عملاء", suppliers: "موردين", products: "منتجات",
    vouchers: "سندات", expenses: "مصروفات", journal: "قيود", payroll: "رواتب",
    cashboxes: "صناديق", warehouses: "مستودعات", banks: "بنوك", "purchase-orders": "أوامر شراء",
    "cheques": "شيكات", quotations: "عروض", batches: "دفعات", users: "مستخدمين",
    companies: "شركات", categories: "تصنيفات", currencies: "عملات",
    "stock-count": "جرد", "fixed-assets": "أصول ثابتة",
  };
  return map[parts[0]] || parts[0];
};

export default function SupportActivityLog() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ method: "all", search: "" });
  const [supportUsers, setSupportUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "500");
      if (selectedUser !== "all") params.set("support_user_id", selectedUser);
      const { data } = await api.get(`/support/activity-log?${params.toString()}`);
      setItems(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [selectedUser]);

  useEffect(() => {
    if (user?.role === "super_admin") {
      api.get("/support-users").then(({ data }) => setSupportUsers(data)).catch(() => {});
    }
  }, [user]);

  const filtered = items.filter(a => {
    if (filter.method !== "all" && a.method !== filter.method) return false;
    if (filter.search) {
      const s = filter.search.toLowerCase();
      return (a.support_user_name || "").toLowerCase().includes(s)
        || (a.company_name || "").toLowerCase().includes(s)
        || (a.path || "").toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div className="space-y-4" data-testid="support-log-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Activity className="w-6 h-6 text-primary" /> سجل نشاط الدعم</h1>
          <p className="text-sm text-muted-foreground mt-1">كل عملية كتابة قام بها موظفو الدعم داخل شركات العملاء (Audit Trail)</p>
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 me-1" /> تحديث</Button>
      </div>

      <div className="flex gap-2 flex-wrap items-end p-3 border rounded-lg bg-muted/20">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground">بحث</label>
          <Input placeholder="اسم موظف / شركة / مسار..." value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })} className="h-9" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">النوع</label>
          <Select value={filter.method} onValueChange={(v) => setFilter({ ...filter, method: v })}>
            <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">الكل</SelectItem>
              <SelectItem value="POST">إنشاء</SelectItem>
              <SelectItem value="PUT">تعديل</SelectItem>
              <SelectItem value="DELETE">حذف</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {user?.role === "super_admin" && supportUsers.length > 0 && (
          <div>
            <label className="text-xs text-muted-foreground">موظف الدعم</label>
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger className="w-48 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الموظفين</SelectItem>
                {supportUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {loading ? <div className="text-center text-muted-foreground py-12">جاري التحميل...</div> :
       filtered.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground">
          <FileText className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <div>لا يوجد نشاط مسجّل</div>
        </div>
       ) : (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b sticky top-0">
              <tr>
                <th className="text-start p-3">التاريخ</th>
                <th className="text-start p-3">الموظف</th>
                <th className="text-start p-3">الشركة</th>
                <th className="text-center p-3">النوع</th>
                <th className="text-start p-3">المسار / الوحدة</th>
                <th className="text-center p-3">الحالة</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id} className="border-b hover:bg-muted/30" data-testid={`log-${a.id}`}>
                  <td className="p-2 text-xs font-mono text-muted-foreground whitespace-nowrap">{new Date(a.at).toLocaleString("ar-EG")}</td>
                  <td className="p-2 font-medium text-xs">{a.support_user_name}</td>
                  <td className="p-2 text-xs">{a.company_name || "—"}</td>
                  <td className="p-2 text-center"><span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold ${METHOD_COLOR[a.method] || "bg-slate-100"}`}>{a.method}</span></td>
                  <td className="p-2 text-xs"><span className="font-medium">{pathLabel(a.path)}</span><span className="text-muted-foreground font-mono ms-2">{a.path}</span></td>
                  <td className="p-2 text-center"><span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${a.status_code < 300 ? "bg-emerald-100 text-emerald-700" : a.status_code < 400 ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"}`}>{a.status_code}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
       )}
      <div className="text-xs text-muted-foreground text-center">إجمالي: {filtered.length} من {items.length}</div>
    </div>
  );
}
